/**
 * Events -> Import CSV (/events/import): bulk-create gun shows and range nights from a spreadsheet.
 *
 * Upload -> Preview (every row validated + matched + duplicate-checked on the server, nothing written) -> Create
 * (one event per call, each its own short transaction) -> Results (per row, downloadable).
 *
 * Per-row, not all-or-nothing: rows with errors are left out and listed; the rest are created. Re-uploading the
 * same file (after fixing the bad rows) is safe — the database derives each event's id from its what/where/when,
 * so rows already created come back "Already imported" and nothing is created twice.
 *
 * Imported events are "scheduled" and marked Incomplete. Nothing is emailed: invitations, reminders and the EIB
 * launch stay in each event's own "Configure Event" flow.
 */
import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../components/common/Button";
import Card from "../components/ui/Card";
import { useToast } from "../components/ui/Toast";
import { useAuth } from "../contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { eventKeys } from "../hooks/useEvents";
import { isFeatureEnabled } from "../utils/featureFlags";
import { TIMEZONE_OPTIONS } from "../utils/constants";
import {
  EVENT_CSV_COLUMNS,
  eventCsvExample,
  eventCsvTemplate,
  parseEventCsv,
  previewToken,
  toCsv,
  type ParsedEventCsv,
} from "../utils/eventCsvImport";
import {
  createImportedEvent,
  isImportUnavailable,
  previewEventImport,
  type CreateResult,
  type PreviewedRow,
} from "../services/eventCsvImport.service";
import { afterEventLinksSaved } from "../services/eventCTAAutoPrepare.service";

type Step = "upload" | "preview" | "creating" | "results";

const TYPE_LABEL: Record<string, string> = {
  gun_show: "Gun show",
  range_night: "Range night",
};

const tzShort = (tz: string | null | undefined): string => {
  const o = TIMEZONE_OPTIONS.find((t) => t.value === tz);
  const m = o ? /\(([^)]+)\)/.exec(o.label) : null;
  return m ? m[1] : (tz ?? "");
};

function download(name: string, text: string) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function whenText(r: PreviewedRow): string {
  const e = r.plan?.event;
  const sd = e?.start_date ?? r.sent.start_date;
  const ed = e?.end_date ?? r.sent.end_date;
  const st = e?.start_time_local ?? r.sent.start_time;
  const et = e?.end_time_local ?? r.sent.end_time;
  const tz = tzShort(e?.timezone ?? r.sent.timezone);
  if (!sd) {
    return "";
  }
  return sd === ed || !ed
    ? `${sd} ${st ?? ""}–${et ?? ""} ${tz}`
    : `${sd} ${st ?? ""} → ${ed} ${et ?? ""} ${tz}`;
}

const STATUS_STYLE: Record<string, string> = {
  ready: "bg-green-100 text-green-800",
  error: "bg-red-100 text-red-800",
  duplicate: "bg-amber-100 text-amber-800",
  already_imported: "bg-gray-100 text-gray-700",
  created: "bg-green-100 text-green-800",
  skipped_duplicate: "bg-amber-100 text-amber-800",
  plan_changed: "bg-red-100 text-red-800",
  skipped: "bg-gray-100 text-gray-700",
};
const STATUS_LABEL: Record<string, string> = {
  ready: "Ready",
  error: "Error",
  duplicate: "Possible duplicate",
  already_imported: "Already imported",
  created: "Created",
  skipped_duplicate: "Skipped (duplicate)",
  plan_changed: "Not created (changed since preview)",
  skipped: "Not created",
};

interface PreviewSnapshot {
  gen: number;
  fileName: string;
  rows: PreviewedRow[];
  token: string;
}

interface QrState {
  running: boolean;
  done: number;
  total: number;
  made: string[];
  notMade: string[];
  flyers: number;
}

const EventImport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("upload");
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState<ParsedEventCsv | null>(null);
  // The ONE preview the Create button may act on: the file name and the exact rows the server planned, bound to
  // the upload generation that produced them and a token over both (audit 73915ddd F1).
  const [snapshot, setSnapshot] = useState<PreviewSnapshot | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [allowDup, setAllowDup] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<CreateResult[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const [qrState, setQrState] = useState<QrState | null>(null);
  // Upload generation: bumped by every file selection and every reset. An async step (reading the file, the
  // preview RPC) may only touch state if its generation is still the current one; late answers are dropped.
  const genRef = useRef(0);

  const previewed = useMemo(() => snapshot?.rows ?? [], [snapshot]);

  const counts = useMemo(() => {
    const c = { ready: 0, error: 0, duplicate: 0, already_imported: 0 };
    previewed.forEach((r) => (c[r.status] += 1));
    return c;
  }, [previewed]);

  const toCreate = useMemo(
    () =>
      previewed.filter(
        (r) =>
          r.status === "ready" ||
          (r.status === "duplicate" && allowDup.has(r.parsed.rowNumber)),
      ),
    [previewed, allowDup],
  );

  if (!isFeatureEnabled("eventCsvImport")) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900">Import events</h1>
        <p className="mt-2 text-gray-600">Event import is not available yet.</p>
      </div>
    );
  }

  const snapshotIsCurrent = (
    snap: PreviewSnapshot | null,
  ): snap is PreviewSnapshot =>
    !!snap &&
    snap.gen === genRef.current &&
    snap.fileName === fileName &&
    previewToken(snap.gen, snap.fileName, snap.rows) === snap.token;

  const clearForNewFile = () => {
    setParsed(null);
    setSnapshot(null);
    setAllowDup(new Set());
    setResults([]);
    setQrState(null);
  };

  const reset = () => {
    genRef.current += 1; // any preview still in flight is now stale
    setStep("upload");
    setFileName("");
    setPreviewing(false);
    clearForNewFile();
    if (fileInput.current) {
      fileInput.current.value = "";
    }
  };

  const onFile = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    const gen = ++genRef.current;
    // Nothing from an earlier file survives a new selection, even for a moment.
    clearForNewFile();
    setFileName(file.name);
    setStep("preview");
    setPreviewing(true);
    try {
      const text = await file.text();
      if (gen !== genRef.current) {
        return;
      }
      const p = parseEventCsv(text);
      setParsed(p);
      if (p.fileErrors.length > 0) {
        return;
      }
      const rows = await previewEventImport(p.rows);
      if (gen !== genRef.current) {
        return;
      }
      setSnapshot({
        gen,
        fileName: file.name,
        rows,
        token: previewToken(gen, file.name, rows),
      });
    } catch (e) {
      if (gen !== genRef.current) {
        return;
      }
      if (isImportUnavailable(e)) {
        setUnavailable(true);
      }
      showToast(
        "error",
        `Could not check the file: ${(e as { message?: string })?.message ?? String(e)}`,
      );
    } finally {
      if (gen === genRef.current) {
        setPreviewing(false);
      }
    }
  };

  const runCreate = async () => {
    const snap = snapshot;
    if (previewing || !snapshotIsCurrent(snap)) {
      showToast(
        "error",
        "The file changed since this preview. Review the current file before creating.",
      );
      return;
    }
    const rows = snap.rows.filter(
      (r) =>
        r.status === "ready" ||
        (r.status === "duplicate" && allowDup.has(r.parsed.rowNumber)),
    );
    if (rows.length === 0) {
      return;
    }
    const batchId = crypto.randomUUID();
    setStep("creating");
    setProgress({ done: 0, total: rows.length });
    const out: CreateResult[] = [];
    for (const r of rows) {
      if (genRef.current !== snap.gen) {
        break; // the file was replaced mid-run (should not be possible from this screen)
      }
      // One call = one short transaction (one events row + its host link); sequential so the events tables are
      // never held for long. The database refuses the row if its plan differs from the previewed digest.
      out.push(
        await createImportedEvent({
          batchId,
          fileName: snap.fileName,
          rowNumber: r.parsed.rowNumber,
          row: r.sent,
          expectedDigest: r.plan?.plan_digest ?? "",
          allowDuplicate: allowDup.has(r.parsed.rowNumber),
        }),
      );
      setProgress({ done: out.length, total: rows.length });
    }
    setResults(out);
    setStep("results");
    void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    const created = out.filter((r) => r.status === "created").length;
    showToast(
      created > 0 ? "success" : "info",
      `${created} event${created === 1 ? "" : "s"} created.`,
    );
  };

  const createdIds = results
    .filter((r) => r.status === "created" && r.event_id)
    .map((r) => r.event_id as string);

  // Runs the wizard's own post-save step per event and reports what it actually did (audit 73915ddd P3):
  // `qr` false means it made no QR now (failed, timed out, or there was nothing to do); those can be retried.
  const prepareQrs = async (ids: string[]) => {
    if (!user?.id || ids.length === 0) {
      return;
    }
    const made = new Set(qrState?.made ?? []);
    const notMade = new Set<string>();
    let flyers = qrState?.flyers ?? 0;
    setQrState({
      running: true,
      done: 0,
      total: ids.length,
      made: [...made],
      notMade: [],
      flyers,
    });
    for (let i = 0; i < ids.length; i++) {
      const r = await afterEventLinksSaved(ids[i], user.id);
      if (r?.qr) {
        made.add(ids[i]);
      } else {
        notMade.add(ids[i]);
      }
      if (r?.flyer) {
        flyers += 1;
      }
      setQrState({
        running: true,
        done: i + 1,
        total: ids.length,
        made: [...made],
        notMade: [...notMade],
        flyers,
      });
    }
    setQrState({
      running: false,
      done: ids.length,
      total: ids.length,
      made: [...made],
      notMade: [...notMade],
      flyers,
    });
  };

  const resultRows = (): string[][] => {
    const byRow = new Map(results.map((r) => [r.row_number, r]));
    return previewed.map((p) => {
      const r = byRow.get(p.parsed.rowNumber);
      const status = r
        ? r.status
        : p.status === "already_imported"
          ? "already_imported"
          : "skipped";
      const eventId = r?.event_id ?? p.plan?.already_imported?.event_id ?? "";
      const notes = [
        ...(r?.errors ?? []),
        ...(r ? [] : p.errors),
        ...(r?.status === "skipped_duplicate" ||
        (!r && p.status === "duplicate")
          ? (p.plan?.duplicates ?? []).map(
              (d) => `existing: ${d.title} ${d.start_local}`,
            )
          : []),
      ].join("; ");
      return [
        String(p.parsed.rowNumber),
        STATUS_LABEL[status] ?? status,
        p.sent.event_type ?? "",
        p.sent.title ?? "",
        eventId,
        eventId ? `${window.location.origin}/events/${eventId}` : "",
        notes,
      ];
    });
  };

  const resultCounts = {
    created: results.filter((r) => r.status === "created").length,
    already:
      results.filter((r) => r.status === "already_imported").length +
      counts.already_imported,
    dup: results.filter((r) => r.status === "skipped_duplicate").length,
    changed: results.filter((r) => r.status === "plan_changed").length,
    error: results.filter((r) => r.status === "error").length,
  };
  const createDisabled =
    previewing ||
    !snapshot ||
    snapshot.gen !== genRef.current ||
    snapshot.fileName !== fileName ||
    toCreate.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Import events from CSV
          </h1>
          <p className="text-gray-600 mt-1">
            Create many gun shows and range nights at once. Nothing is emailed:
            each event is created as{" "}
            <span className="font-medium">Incomplete</span> and is launched from
            its own Configure Event step.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate("/events?view=list")}
        >
          ← Back to events
        </Button>
      </div>

      {unavailable && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Event import is not set up on this environment yet (the database part
          is missing). Please contact the CRM team.
        </div>
      )}

      {step === "upload" && (
        <Card>
          <div className="p-6 space-y-5" data-testid="event-import-upload">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() =>
                  download("event-import-template.csv", eventCsvTemplate())
                }
              >
                Download template
              </Button>
              <Button
                variant="secondary"
                onClick={() =>
                  download("event-import-example.csv", eventCsvExample())
                }
              >
                Download example (3 events)
              </Button>
            </div>
            <label
              className="block border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer hover:border-primary-400"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                void onFile(e.dataTransfer.files?.[0]);
              }}
            >
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                data-testid="event-import-file"
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
              <div className="text-gray-700 font-medium">
                Choose a CSV file or drop it here
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Save your spreadsheet as CSV. Up to 500 events per file. Nothing
                is created until you review the preview and click Create.
              </div>
            </label>
            <details className="text-sm">
              <summary className="cursor-pointer font-medium text-gray-800">
                Columns
              </summary>
              <table className="mt-3 w-full text-left">
                <thead>
                  <tr className="text-gray-500">
                    <th className="py-1 pr-4">Column</th>
                    <th className="py-1 pr-4">Required</th>
                    <th className="py-1">What to put</th>
                  </tr>
                </thead>
                <tbody>
                  {EVENT_CSV_COLUMNS.map((c) => (
                    <tr
                      key={c.key}
                      className="border-t border-gray-100 align-top"
                    >
                      <td className="py-1 pr-4 font-mono text-xs">{c.key}</td>
                      <td className="py-1 pr-4 whitespace-nowrap">
                        {c.required === "always"
                          ? "Yes"
                          : c.required === "gun_show"
                            ? "Gun shows"
                            : c.required === "one_of_org"
                              ? "One of the three"
                              : "No"}
                      </td>
                      <td className="py-1 text-gray-700">{c.help}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </div>
        </Card>
      )}

      {step === "preview" && (
        <Card>
          <div className="p-6 space-y-4" data-testid="event-import-preview">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{fileName}</span>
                {parsed && parsed.fileErrors.length === 0 && !previewing && (
                  <>
                    {" "}
                    — {previewed.length} row{previewed.length === 1 ? "" : "s"}:{" "}
                    <span className="text-green-700">{counts.ready} ready</span>
                    ,{" "}
                    <span className="text-amber-700">
                      {counts.duplicate} possible duplicates
                    </span>
                    ,{" "}
                    <span className="text-gray-600">
                      {counts.already_imported} already imported
                    </span>
                    ,{" "}
                    <span className="text-red-700">
                      {counts.error} with errors
                    </span>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={reset}>
                  Choose a different file
                </Button>
                <Button
                  onClick={() => void runCreate()}
                  disabled={createDisabled}
                  data-testid="event-import-create"
                >
                  Create {toCreate.length} event
                  {toCreate.length === 1 ? "" : "s"}
                </Button>
              </div>
            </div>

            {parsed?.fileErrors.map((e) => (
              <div
                key={e}
                className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              >
                {e}
              </div>
            ))}
            {parsed && parsed.unknownHeaders.length > 0 && (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                Ignored column{parsed.unknownHeaders.length > 1 ? "s" : ""}:{" "}
                {parsed.unknownHeaders.join(", ")}
              </div>
            )}
            {counts.error > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Rows with errors will not be created. Fix them in your
                spreadsheet and upload the same file again — events already
                created are recognised and skipped, never created twice.
              </div>
            )}
            {previewing && (
              <div className="text-sm text-gray-600">Checking every row…</div>
            )}

            {!previewing && previewed.length > 0 && (
              <div className="overflow-x-auto">
                <table
                  className="min-w-full text-sm"
                  data-testid="event-import-table"
                >
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-3">Row</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Type</th>
                      <th className="py-2 pr-3">Title</th>
                      <th className="py-2 pr-3">When (venue time)</th>
                      <th className="py-2 pr-3">Promoter / host</th>
                      <th className="py-2 pr-3">Venue</th>
                      <th className="py-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewed.map((r) => (
                      <tr
                        key={r.parsed.rowNumber}
                        className="border-b border-gray-100 align-top"
                      >
                        <td className="py-2 pr-3 text-gray-500">
                          {r.parsed.rowNumber}
                        </td>
                        <td className="py-2 pr-3">
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_STYLE[r.status]}`}
                          >
                            {STATUS_LABEL[r.status]}
                          </span>
                          {r.status === "duplicate" && (
                            <label className="mt-1 flex items-center gap-1 text-xs text-gray-700">
                              <input
                                type="checkbox"
                                checked={allowDup.has(r.parsed.rowNumber)}
                                onChange={(e) => {
                                  const next = new Set(allowDup);
                                  if (e.target.checked) {
                                    next.add(r.parsed.rowNumber);
                                  } else {
                                    next.delete(r.parsed.rowNumber);
                                  }
                                  setAllowDup(next);
                                }}
                              />
                              Create anyway
                            </label>
                          )}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {TYPE_LABEL[r.sent.event_type ?? ""] ??
                            r.parsed.raw.event_type}
                        </td>
                        <td className="py-2 pr-3">
                          {r.sent.title ?? r.parsed.raw.title}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {whenText(r)}
                          {r.timezoneSource && r.timezoneSource !== "row" && (
                            <div className="text-xs text-gray-500">
                              time zone from venue{" "}
                              {r.timezoneSource === "zip" ? "ZIP" : "state"}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          {r.plan?.organization ? (
                            <>
                              {r.plan.organization.name}
                              {r.plan.organization.facility_id && (
                                <div className="text-xs text-gray-500">
                                  facility {r.plan.organization.facility_id}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400">
                              {r.sent.org_name ??
                                r.sent.org_facility_id ??
                                r.sent.org_affiliate_id ??
                                ""}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          {r.plan?.venue?.name ?? r.sent.venue_name}
                          <div className="text-xs text-gray-500">
                            {[
                              r.plan?.venue?.city ?? r.sent.venue_city,
                              r.plan?.venue?.state ?? r.sent.venue_state,
                            ]
                              .filter(Boolean)
                              .join(", ")}{" "}
                            {r.plan?.venue?.postal_code ?? r.sent.venue_zip}
                            {r.plan?.venue &&
                              r.plan.venue.source !== "row" &&
                              " (from host)"}
                          </div>
                        </td>
                        <td className="py-2 text-xs">
                          {r.errors.map((e) => (
                            <div key={e} className="text-red-700">
                              {e}
                            </div>
                          ))}
                          {r.status === "already_imported" &&
                            r.plan?.already_imported && (
                              <div className="text-gray-700">
                                Created by an earlier import:{" "}
                                <Link
                                  className="text-primary-600 underline"
                                  to={`/events/${r.plan.already_imported.event_id}`}
                                >
                                  {r.plan.already_imported.title}
                                </Link>
                                {r.plan.already_imported.is_deleted &&
                                  " (since deleted)"}
                              </div>
                            )}
                          {r.status === "duplicate" &&
                            r.plan?.duplicates.map((d) => (
                              <div key={d.event_id} className="text-amber-800">
                                Already in the CRM:{" "}
                                <Link
                                  className="underline"
                                  to={`/events/${d.event_id}`}
                                >
                                  {d.title}
                                </Link>{" "}
                                ({d.start_local})
                              </div>
                            ))}
                          {r.warnings.map((w) => (
                            <div key={w} className="text-amber-700">
                              {w}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      )}

      {step === "creating" && (
        <Card>
          <div className="p-6" data-testid="event-import-creating">
            <div className="text-gray-800 font-medium">
              Creating event {Math.min(progress.done + 1, progress.total)} of{" "}
              {progress.total}…
            </div>
            <div className="mt-3 h-2 w-full rounded bg-gray-200">
              <div
                className="h-2 rounded bg-primary-600"
                style={{
                  width: `${progress.total ? (100 * progress.done) / progress.total : 0}%`,
                }}
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Keep this page open until it finishes.
            </p>
          </div>
        </Card>
      )}

      {step === "results" && (
        <Card>
          <div className="p-6 space-y-4" data-testid="event-import-results">
            <div className="text-gray-800">
              <span className="font-medium text-green-700">
                {resultCounts.created} created
              </span>
              {resultCounts.already > 0 && (
                <>, {resultCounts.already} already imported</>
              )}
              {resultCounts.dup > 0 && (
                <>, {resultCounts.dup} skipped as duplicates</>
              )}
              {resultCounts.changed > 0 && (
                <span className="text-red-700">
                  , {resultCounts.changed} not created because details changed
                  since the preview (upload the file again to review them)
                </span>
              )}
              {resultCounts.error > 0 && (
                <span className="text-red-700">
                  , {resultCounts.error} failed
                </span>
              )}
              {counts.error > 0 && (
                <>, {counts.error} not created (errors in the file)</>
              )}
              .
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() =>
                  download(
                    `${fileName.replace(/\.csv$/i, "")}-results.csv`,
                    toCsv(
                      [
                        "row",
                        "result",
                        "event_type",
                        "title",
                        "event_id",
                        "link",
                        "notes",
                      ],
                      resultRows(),
                    ),
                  )
                }
              >
                Download results
              </Button>
              {createdIds.length > 0 && !qrState && (
                <Button
                  variant="secondary"
                  onClick={() => void prepareQrs(createdIds)}
                >
                  {`Prepare QR codes and flyers for the ${createdIds.length} new event${createdIds.length === 1 ? "" : "s"}`}
                </Button>
              )}
              {qrState?.running && (
                <Button variant="secondary" disabled>
                  {`Preparing QR codes and flyers… ${qrState.done} of ${qrState.total}`}
                </Button>
              )}
              {qrState && !qrState.running && qrState.notMade.length > 0 && (
                <Button
                  variant="secondary"
                  onClick={() => void prepareQrs(qrState.notMade)}
                  data-testid="event-import-qr-retry"
                >
                  {`Retry QR codes for ${qrState.notMade.length} event${qrState.notMade.length === 1 ? "" : "s"}`}
                </Button>
              )}
              <Button variant="secondary" onClick={reset}>
                Import another file
              </Button>
              <Button onClick={() => navigate("/events?view=list")}>
                Go to events
              </Button>
            </div>
            {qrState && !qrState.running && (
              <p
                className="text-sm text-gray-700"
                data-testid="event-import-qr-summary"
              >
                QR codes made: {qrState.made.length} of {createdIds.length};
                flyers made: {qrState.flyers}.
                {qrState.notMade.length > 0 &&
                  ` ${qrState.notMade.length} not made now (failed, timed out, or nothing to do) - retry, or open the event's Call to Action panel, which finishes anything missing.`}
              </p>
            )}
            <p className="text-sm text-gray-600">
              The new events show as{" "}
              <span className="font-medium">Incomplete</span> in the events
              list. Open each one and use Configure Event to finish setup and
              launch invitations.
            </p>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">Row</th>
                  <th className="py-2 pr-3">Result</th>
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {resultRows().map((r) => (
                  <tr key={r[0]} className="border-b border-gray-100 align-top">
                    <td className="py-2 pr-3 text-gray-500">{r[0]}</td>
                    <td className="py-2 pr-3">{r[1]}</td>
                    <td className="py-2 pr-3">
                      {r[4] ? (
                        <Link
                          className="text-primary-600 underline"
                          to={`/events/${r[4]}`}
                        >
                          {r[3]}
                        </Link>
                      ) : (
                        r[3]
                      )}
                    </td>
                    <td className="py-2 text-xs text-gray-700">{r[6]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default EventImport;
